import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
} from '@mui/material';
// Ícones removidos conforme solicitado
import { useTheme } from '@mui/material/styles';

// Componentes locais
import WhatsAppConnectionCard from './WhatsAppConnectionCard';
import QRCodeModal from './QRCodeModal';
import WhatsAppNotifications from './WhatsAppNotifications';

// Hooks
import {
  useWhatsAppConnectionsHybrid,
  useConnectWhatsApp,
  useDisconnectWhatsApp,
  useRestartWhatsApp,
} from '../../hooks/api/whatsappApiHooks';
import { useWhatsAppSSE } from '../../hooks/useSSE';
import { useWhatsAppNotifications } from './WhatsAppNotifications';

/**
 * Componente principal para gerenciar conexões WhatsApp
 * Seguindo padrões arquiteturais do projeto JBE Engenharia
 */
const WhatsAppManager = () => {
  const theme = useTheme();
  
  // Estados locais
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});

  // Hooks de API
  const {
    connectionsLoading,
    connections,
    connectionsError,
    refetchConnections,
    isWebSocketConnected,
  } = useWhatsAppConnectionsHybrid();

  const connectMutation = useConnectWhatsApp();
  const disconnectMutation = useDisconnectWhatsApp();
  const restartMutation = useRestartWhatsApp();

  // SSE para updates em tempo real
  const {
    lastEvent,
    notifications,
    removeNotification,
  } = useWhatsAppSSE();

  // Sistema de notificações
  const { showGenericNotification } = useWhatsAppNotifications();

  // Atualiza estado de loading
  const setConnectionLoading = (connectionId, action, loading) => {
    setLoadingStates(prev => ({
      ...prev,
      [connectionId]: loading ? action : null,
    }));
  };

  // Handler para conectar instância
  const handleConnect = async (connectionId) => {
    try {
      setConnectionLoading(connectionId, 'connect', true);
      setSelectedConnectionId(connectionId);
      
      const result = await connectMutation.mutateAsync(connectionId);
      
      if (result.qrCode) {
        setQrCodeData(result);
        setQrModalOpen(true);
        showGenericNotification(
          `QR Code gerado para ${result.instanceName}. Escaneie para conectar.`,
          'info'
        );
      }
    } catch (error) {
      console.error('Erro ao conectar WhatsApp:', error);
      showGenericNotification(
        'Erro ao iniciar conexão WhatsApp. Tente novamente.',
        'error'
      );
    } finally {
      setConnectionLoading(connectionId, 'connect', false);
    }
  };

  // Handler para desconectar instância
  const handleDisconnect = async (connectionId) => {
    try {
      setConnectionLoading(connectionId, 'disconnect', true);
      
      const result = await disconnectMutation.mutateAsync(connectionId);
      
      showGenericNotification(
        result.message || 'WhatsApp desconectado com sucesso.',
        'success'
      );
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp:', error);
      showGenericNotification(
        'Erro ao desconectar WhatsApp. Tente novamente.',
        'error'
      );
    } finally {
      setConnectionLoading(connectionId, 'disconnect', false);
    }
  };

  // Handler para reiniciar instância
  const handleRestart = async (connectionId) => {
    try {
      setConnectionLoading(connectionId, 'restart', true);
      
      const result = await restartMutation.mutateAsync(connectionId);
      
      showGenericNotification(
        result.message || 'WhatsApp reiniciado com sucesso.',
        'success'
      );
    } catch (error) {
      console.error('Erro ao reiniciar WhatsApp:', error);
      showGenericNotification(
        'Erro ao reiniciar WhatsApp. Tente novamente.',
        'error'
      );
    } finally {
      setConnectionLoading(connectionId, 'restart', false);
    }
  };

  // Handler para fechar modal QR
  const handleCloseQRModal = () => {
    setQrModalOpen(false);
    setQrCodeData(null);
    setSelectedConnectionId(null);
  };

  // Handler para refresh QR Code
  const handleRefreshQR = () => {
    if (selectedConnectionId) {
      handleConnect(selectedConnectionId);
    }
  };

  // Determina status atual da conexão selecionada
  const getSelectedConnectionStatus = () => {
    if (!selectedConnectionId || !connections) return 'connecting';
    
    const connection = connections.find(c => c.id === selectedConnectionId);
    return connection?.status || 'connecting';
  };

  // Atualiza dados quando SSE recebe eventos
  useEffect(() => {
    if (lastEvent?.type === 'connection_status_changed') {
      // Se conectou com sucesso e modal está aberto, fecha após delay
      if (lastEvent.status === 'open' && qrModalOpen) {
        setTimeout(() => {
          handleCloseQRModal();
        }, 2000);
      }
      
      // Recarrega dados das conexões
      refetchConnections();
    }
  }, [lastEvent, qrModalOpen, refetchConnections]);

  if (connectionsLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Carregando conexões WhatsApp...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Indicador de status WebSocket */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: isWebSocketConnected ? 'success.main' : 'warning.main',
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {isWebSocketConnected 
            ? 'Conectado em tempo real via WebSocket' 
            : 'Modo fallback: atualizações a cada 30s'
          }
        </Typography>
      </Box>

      {/* Erro de carregamento */}
      {connectionsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Erro ao carregar conexões: {connectionsError.message}
          </Typography>
          <Button 
            size="small" 
            onClick={refetchConnections}
            sx={{ mt: 1 }}
          >
            Tentar Novamente
          </Button>
        </Alert>
      )}

      {/* Lista de conexões */}
      {connections?.length > 0 ? (
        <Grid container spacing={3}>
          {connections.map((connection) => (
            <Grid item xs={12} sm={6} md={4} key={connection.id}>
              <WhatsAppConnectionCard
                connection={connection}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onRestart={handleRestart}
                loading={!!loadingStates[connection.id]}
                loadingAction={loadingStates[connection.id]}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Nenhuma conexão WhatsApp encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure uma instância WhatsApp no backend para começar a usar.
          </Typography>
        </Paper>
      )}

      {/* Modal QR Code */}
      <QRCodeModal
        open={qrModalOpen}
        onClose={handleCloseQRModal}
        qrCodeData={qrCodeData}
        connectionStatus={getSelectedConnectionStatus()}
        instanceName={qrCodeData?.instanceName}
        onRefresh={handleRefreshQR}
        refreshing={loadingStates[selectedConnectionId] === 'connect'}
      />

      {/* Sistema de notificações */}
      <WhatsAppNotifications
        notifications={notifications}
        onProcessed={removeNotification}
      />
    </Container>
  );
};

export default WhatsAppManager;
